import { useRouter } from "next/router";
import { ImageStyles } from "@business-finland/wif-ui-lib";
import {
  MasonryGrid,
  type ILinkPreview,
  type IRecommendationsCardProps,
} from "@business-finland/wif-ui-lib/dist/Masonry";

import { Viewport } from "../../../constants/app";
import useImageDataHandler, {
  ImageDataHandlerFn,
} from "../../../hooks/useImageDataHandler";
import { formatPageLink, jcrMultiFieldToArray } from "../../../util/magnolia";

interface ICard {
  id: string;
  cardTitle: string;
  cardIntroText: string;
  link: string;
  linkLabel: string;
  linkDescription: string;
  altText?: string;
  image?: Wif.Mgnl.Image;
}

interface ILinkPreviewVariant extends Wif.Mgnl.Node {
  hasSaveButton: boolean;
  card?: Wif.Mgnl.Node<{
    cardTitle: string;
    cardIntroText: string;
    link: string;
    linkLabel: string;
    linkDescription: string;
    altText?: string;
    image?: Wif.Mgnl.Image;
    field: string;
  }>;
}

interface IMasonryCardVariant extends Wif.Mgnl.Node {
  page: Wif.Mgnl.Node<Wif.Mgnl.NavPage>;
}
interface IMasonryProps {
  variants: Wif.Mgnl.NodeWithList<
    Wif.Mgnl.Node<{
      field: string;
      page?: Wif.Mgnl.Node<Wif.Mgnl.NavPage>;
      hasSaveButton?: boolean;
      card?: Wif.Mgnl.Node<
        ICard & {
          field: string;
        }
      >;
    }>
  >;
}

export function Masonry(props: IMasonryProps) {
  const { variants: rawVariants } = props;

  const { isPreview } = useRouter();
  const imageDataHandler = useImageDataHandler();

  const variants = jcrMultiFieldToArray(rawVariants).map((variant) => {
    if (!("page" in variant)) {
      return {
        linkPreview: mapToLinkPreviewCardProps(
          variant as ILinkPreviewVariant,
          imageDataHandler
        ),
      };
    }

    return {
      masonry: mapToMasonryCardProps(
        variant as IMasonryCardVariant,
        isPreview,
        imageDataHandler
      ),
    };
  });

  return <MasonryGrid variants={variants} />;
}

const mapToMasonryCardProps = (
  variant: IMasonryCardVariant,
  isPreview: boolean,
  imageDataHandler: ImageDataHandlerFn
): IRecommendationsCardProps => {
  const { page } = variant;
  const { title, description, introductionImage } = page;

  const imageData = imageDataHandler(introductionImage);
  const imageProps = {
    ...imageData,
    altText: title,
    styling: ImageStyles.MASONRY_CARD,
    credits: introductionImage?.metadata?.rights,
  };

  return {
    title,
    description,
    link: formatPageLink(page["@path"], isPreview),
    image: imageProps,
  };
};

const mapToLinkPreviewCardProps = (
  linkPreviewVariant: ILinkPreviewVariant,
  imageDataHandler: ImageDataHandlerFn
): ILinkPreview | undefined => {
  if (!linkPreviewVariant.card) {
    return undefined;
  }

  const card = mapToCardProps(linkPreviewVariant.card, imageDataHandler);

  return {
    card,
    hasSaveButton: false,
    hasImages: linkPreviewVariant?.card?.field === "image",
  };
};

function mapToCardProps(
  node: Wif.Mgnl.Node<ICard>,
  imageDataHandler: ImageDataHandlerFn
): ILinkPreview["card"] {
  const {
    image,
    cardTitle,
    cardIntroText,
    link,
    altText,
    linkLabel,
    linkDescription,
  } = node;

  if (!image || !altText) {
    return {
      cardTitle,
      cardIntroText,
      link,
      linkLabel,
      linkDescription,
    };
  }

  const imageData = imageDataHandler(image, { maxViewport: Viewport.sm });

  const imageProps = {
    ...imageData,
    altText,
    credits: image?.metadata?.rights,
  };
  return {
    cardTitle,
    cardIntroText,
    link,
    linkLabel,
    linkDescription,
    imageProps,
  };
}
